import { Button, Input, InputRef, Row, Spin, Tag, theme } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { CSSProperties, useRef, useState } from "react"
import { useCaseUserLabels } from "../../hooks/useCaseUserLabels.ts"
import { USER_TAG_LIMIT } from "../../backend/constants.ts"

const TAG_INPUT_STYLE: CSSProperties = {
    width: 64,
    height: 22,
    marginInlineEnd: 8,
    verticalAlign: 'top',
}

/**
 * Displays and manages user labels for a specific record within a case.
 * This component allows for viewing, adding, and deleting custom labels
 * associated with a given record.
 *
 * @component
 * @param {Object} props The component props.
 * @param {string} props.recordId The ID of the record for which labels are managed, It might be an address id,
 *                                entity id, report id, money flow id, case id, etc.
 * @param {boolean} [props.editable=true] Indicates if the labels can be edited.
 *
 */
export const UserLabelsList = ({ recordId, editable = true }: { recordId: string, editable?: boolean }) => {
    const { token } = theme.useToken()
    const [inputVisible, setInputVisible] = useState(false)
    const {
        userLabels,
        updateLabel,
        isPending,
        isFetching,
        variables
    } = useCaseUserLabels()
    const itemUserLabels = userLabels[recordId] ?? []

    const inputRef = useRef<InputRef>(null)

    const handleDeleteLabel = (idx: number) => {
        updateLabel({ id: recordId, value: itemUserLabels.toSpliced(idx, 1) })
    }

    const handlePressEnter = () => {
        const newLabel = inputRef?.current?.input?.value
        if (!newLabel || itemUserLabels.includes(newLabel)) return
        updateLabel({ id: recordId, value: [...itemUserLabels, newLabel] })
        setInputVisible(false)
    }

    return (
        <Row gutter={[token.marginXXS, token.marginXXS]}>
            {((isPending || isFetching) && variables?.value ?
                variables!.value :
                itemUserLabels).map((itemLabel, labelIdx) => <Tag
                style={{ maxWidth: "100%", overflow: "auto" }}
                className={"scroll"}
                closable={editable}
                color="purple"
                key={itemLabel + labelIdx}
                onClose={() => handleDeleteLabel(labelIdx)}
            >
                {itemLabel}
            </Tag>
            )}
            {(isPending || isFetching) && <Spin size="small"/>}
            {editable && (inputVisible ? (
                <Input
                    type="text"
                    size="small"
                    ref={inputRef}
                    style={TAG_INPUT_STYLE}
                    autoFocus={true}
                    onPressEnter={handlePressEnter}
                    onBlur={() => setInputVisible(false)}/>
            ) : (
                itemUserLabels.length < USER_TAG_LIMIT && <Button
                    style={{ height: '22px', fontSize: 12 }}
                    size="small"
                    type="dashed"
                    icon={<PlusOutlined style={{ fontSize: 10 }}/>}
                    onClick={() => setInputVisible(true)}
                >
                    New Tag
                </Button>
            ))}
        </Row>
    )
}
